import { useCallback, useEffect, useState } from "react";
import {
  STATS_METRICS,
  STATS_SCALES,
  type StatsMetric,
  type StatsScale,
  type StatsSeriesResponse,
  type StatsSummary,
} from "@shared";
import { Barres, COULEURS, LIBELLES, TIRETS, Courbe } from "./charts";
import * as api from "./statsApi";

const LIBELLE_ECHELLE: Record<StatsScale, string> = {
  hour: "Heure",
  day: "Jour",
  week: "Semaine",
  month: "Mois",
};

/**
 * Fenêtre affichée EN PLUS de l'échelle, pour ne pas noyer un graphique fin
 * (heure, jour) sous tout l'historique de la mission. Filtrée côté client :
 * le volume d'une fête (quelques dizaines de parties) s'y prête, et ça évite
 * de dupliquer la logique de fenêtre côté serveur.
 *
 * Semaine et mois n'ont pas cette plage : demandée seulement pour heure et
 * jour. Le mois a son propre filtre, par année — voir plus bas.
 */
type Plage = "day" | "week" | "month" | "year" | "all";

const PLAGES_PAR_ECHELLE: Partial<Record<StatsScale, Plage[]>> = {
  hour: ["day", "week", "month", "year", "all"],
  day: ["week", "month", "year", "all"],
};

const PLAGE_PAR_DEFAUT: Partial<Record<StatsScale, Plage>> = {
  // Une fête tient rarement plus d'une journée : zoomer par défaut sur
  // aujourd'hui évite un graphique par heure illisible sur tout l'historique.
  hour: "day",
  day: "all",
};

const LIBELLE_PLAGE: Record<Plage, string> = {
  day: "Aujourd'hui",
  week: "Cette semaine",
  month: "Ce mois-ci",
  year: "Cette année",
  all: "Tout",
};

/** Début de la plage, en millisecondes UNIX ; `null` pour « tout ». */
function debutPlage(plage: Plage): number | null {
  if (plage === "all") return null;
  const d = new Date();
  if (plage === "day") {
    d.setHours(0, 0, 0, 0);
  } else if (plage === "week") {
    d.setHours(0, 0, 0, 0);
    const jour = (d.getDay() + 6) % 7; // lundi = 0, même convention que le serveur
    d.setDate(d.getDate() - jour);
  } else if (plage === "month") {
    d.setDate(1);
    d.setHours(0, 0, 0, 0);
  } else {
    d.setMonth(0, 1);
    d.setHours(0, 0, 0, 0);
  }
  return d.getTime();
}

/**
 * Le tableau de bord, une fois le mot de passe accepté.
 *
 * Il COMPTE, il ne nomme personne : ni pseudo ni adresse courriel n'arrivent
 * jusqu'ici. Le téléchargement des participants au concours vit dans
 * l'administration, derrière l'autre mot de passe.
 *
 * Interface en français seulement, comme l'administration : elle s'adresse à
 * l'organisateur, pas aux jeunes.
 */
export default function StatsApp({ onLogout }: { onLogout: () => void }) {
  const [scale, setScale] = useState<StatsScale>("day");
  const [plage, setPlage] = useState<Plage>("day");
  // null = pas encore choisie : on retombe sur la plus récente avec des données.
  const [annee, setAnnee] = useState<number | null>(null);
  const [serie, setSerie] = useState<StatsSeriesResponse | null>(null);
  const [resume, setResume] = useState<StatsSummary | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [tableau, setTableau] = useState(false);
  // Toutes visibles au départ : on découvre la forme générale, puis on isole.
  const [visibles, setVisibles] = useState<StatsMetric[]>([...STATS_METRICS]);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const [s, r] = await Promise.all([api.fetchSeries(scale), api.fetchSummary()]);
      setSerie(s);
      setResume(r);
    } catch {
      setErreur("Impossible de charger les statistiques. La mission est-elle active ?");
    }
  }, [scale]);

  useEffect(() => {
    void charger();
  }, [charger]);

  // L'ordre figé de STATS_METRICS est conservé : c'est lui qui garantit la
  // séparation des couleurs voisines.
  const mesures = STATS_METRICS.filter((m) => visibles.includes(m));

  function basculer(m: StatsMetric): void {
    setVisibles((v) => (v.includes(m) ? v.filter((x) => x !== m) : [...v, m]));
  }

  // La plage choisie peut ne plus exister après un changement d'échelle
  // (ex. "cette semaine" en venant de l'heure vers la semaine) : on retombe
  // alors sur le défaut de la nouvelle échelle plutôt que de planter le filtre.
  const plagesDisponibles = PLAGES_PAR_ECHELLE[scale];
  const plageEffective: Plage =
    plagesDisponibles?.includes(plage) === true ? plage : (PLAGE_PAR_DEFAUT[scale] ?? "all");

  const anneesDisponibles = [
    ...new Set((serie?.points ?? []).map((p) => new Date(p.bucket).getFullYear())),
  ].sort((a, b) => b - a);
  const anneeEffective = annee !== null && anneesDisponibles.includes(annee)
    ? annee
    : (anneesDisponibles[0] ?? new Date().getFullYear());

  const points = (() => {
    if (serie === null) return [];
    if (scale === "month") {
      return serie.points.filter((p) => new Date(p.bucket).getFullYear() === anneeEffective);
    }
    const debut = debutPlage(plageEffective);
    return debut === null ? serie.points : serie.points.filter((p) => Date.parse(p.bucket) >= debut);
  })();

  // Les cartouches de tête doivent refléter la plage affichée, pas tout
  // l'historique : sinon le chiffre au-dessus du graphique contredirait ce
  // que le graphique montre.
  const totaux: Record<StatsMetric, number> =
    serie === null || points.length === serie.points.length
      ? (serie?.totals ??
        (Object.fromEntries(STATS_METRICS.map((m) => [m, 0])) as Record<StatsMetric, number>))
      : (Object.fromEntries(
          STATS_METRICS.map((m) => [m, points.reduce((s, p) => s + p.counts[m], 0)]),
        ) as Record<StatsMetric, number>);

  return (
    <div className="stats">
      <div className="stats__barre">
        <div className="tabs">
          {STATS_SCALES.map((s) => (
            <button
              key={s}
              type="button"
              className={`tabs__button ${scale === s ? "tabs__button--active" : ""}`}
              onClick={() => {
                setScale(s);
              }}
            >
              {LIBELLE_ECHELLE[s]}
            </button>
          ))}
        </div>
        <div className="button-row">
          <button type="button" className="button button--small" onClick={() => void charger()}>
            ↻ Actualiser
          </button>
          <button type="button" className="button button--small" onClick={onLogout}>
            Se déconnecter
          </button>
        </div>
      </div>

      {erreur !== null && <p className="form__error">{erreur}</p>}
      {serie === null || resume === null ? (
        <p className="panel__hint">Chargement…</p>
      ) : (
        <>
          {/* Les totaux d'abord : quelques nombres répondent à la plupart des
              questions sans qu'on ait à lire une courbe. */}
          <section className="stats__cartouches">
            {STATS_METRICS.map((m) => (
              <div className="cartouche" key={m}>
                <span className="cartouche__valeur" style={{ color: COULEURS[m] }}>
                  {totaux[m]}
                </span>
                <span className="cartouche__nom">{LIBELLES[m]}</span>
              </div>
            ))}
          </section>

          <section className="panel">
            <h2 className="panel__title">Évolution dans le temps</h2>

            {/* Plage affichée EN PLUS de l'échelle (heure, jour) : elle évite
                de noyer un graphique fin sous tout l'historique de la
                mission. Absente pour semaine, remplacée par un choix
                d'année pour mois. */}
            {plagesDisponibles !== undefined && (
              <div className="tabs tabs--small">
                {plagesDisponibles.map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`tabs__button ${plageEffective === p ? "tabs__button--active" : ""}`}
                    onClick={() => {
                      setPlage(p);
                    }}
                  >
                    {LIBELLE_PLAGE[p]}
                  </button>
                ))}
              </div>
            )}

            {scale === "month" && anneesDisponibles.length > 0 && (
              <div className="tabs tabs--small">
                {anneesDisponibles.map((a) => (
                  <button
                    key={a}
                    type="button"
                    className={`tabs__button ${anneeEffective === a ? "tabs__button--active" : ""}`}
                    onClick={() => {
                      setAnnee(a);
                    }}
                  >
                    {a}
                  </button>
                ))}
              </div>
            )}

            {/* Légende TOUJOURS présente, et elle sert aussi de filtre. Chaque
                entrée montre le trait exact de sa courbe, pointillés compris :
                l'identité ne tient jamais à la couleur seule. */}
            <ul className="legende">
              {STATS_METRICS.map((m) => (
                <li key={m}>
                  <button
                    type="button"
                    className={`legende__item ${visibles.includes(m) ? "" : "legende__item--off"}`}
                    aria-pressed={visibles.includes(m)}
                    onClick={() => {
                      basculer(m);
                    }}
                  >
                    <svg width="26" height="10" aria-hidden="true">
                      <line
                        x1="1"
                        y1="5"
                        x2="25"
                        y2="5"
                        stroke={COULEURS[m]}
                        strokeWidth="2"
                        strokeDasharray={TIRETS[m] || undefined}
                      />
                    </svg>
                    {LIBELLES[m]}
                  </button>
                </li>
              ))}
            </ul>

            <Courbe points={points} scale={scale} mesures={mesures} />

            <button
              type="button"
              className="button button--small"
              aria-expanded={tableau}
              onClick={() => {
                setTableau(!tableau);
              }}
            >
              {tableau ? "Masquer le tableau" : "Voir les chiffres en tableau"}
            </button>

            {/* Une vue tableau existe toujours : un graphique n'est pas lisible
                par tout le monde, et les chiffres exacts se copient. */}
            {tableau && (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Période</th>
                      {mesures.map((m) => (
                        <th key={m}>{LIBELLES[m]}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {points.map((p) => (
                      <tr key={p.bucket}>
                        <td>{api.formatBucket(p.bucket, scale)}</td>
                        {mesures.map((m) => (
                          <td key={m}>{p.counts[m]}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <section className="panel">
            <h2 className="panel__title">Où les jeunes s'arrêtent</h2>
            <p className="panel__hint">
              Chaque étape compte des <strong>joueurs distincts</strong>, pas des parties. L'écart
              entre deux marches dit où le parcours se perd.
            </p>
            <Barres
              titre=""
              lignes={resume.funnel.map((e) => ({ nom: e.etape, valeur: e.joueurs }))}
              couleur={COULEURS.users}
            />
          </section>

          <div className="stats__colonnes">
            <section className="panel">
              <Barres
                titre="Objets les moins trouvés"
                lignes={resume.objets.map((o) => ({
                  nom: o.nom,
                  valeur: o.joueurs,
                  note: `${o.pourcent} %`,
                }))}
                couleur={COULEURS.expedition1}
              />
              <p className="panel__hint">
                Un objet trouvé par une petite part des jeunes est mal placé, ou n'est plus là.
                C'est la mesure à regarder <strong>pendant</strong> l'événement.
              </p>
            </section>

            <section className="panel">
              <Barres
                titre="Leurres les plus cochés"
                lignes={resume.leurres.map((l) => ({
                  nom: l.nom,
                  valeur: l.joueurs,
                  note: `${l.pourcent} %`,
                }))}
                couleur={COULEURS.mission}
              />
              <p className="panel__hint">
                Les faux amis qui piègent vraiment. Un leurre jamais coché ne sert à rien.
              </p>
            </section>
          </div>

          <section className="panel">
            <Barres
              titre="Associations qui résistent"
              lignes={resume.associations.map((a) => ({
                nom: a.nom,
                valeur: a.rates,
                // L'unité est dans la note : elle doit s'accorder, et « 1
                // essais ratés » se remarque tout de suite.
                note: `essai${a.rates > 1 ? "s" : ""} raté${a.rates > 1 ? "s" : ""} · ${a.resolue} l'ont trouvée`,
              }))}
              couleur={COULEURS.expedition2}
            />
          </section>

          <section className="panel">
            <h2 className="panel__title">Durées et affluence</h2>
            <div className="stats__cartouches">
              {(["expedition1", "expedition2"] as const).map((jeu) => (
                <div className="cartouche" key={jeu}>
                  <span className="cartouche__valeur">
                    {api.formatDuree(resume.durees[jeu].medianeSecondes)}
                  </span>
                  <span className="cartouche__nom">
                    {LIBELLES[jeu]} — durée médiane
                    <br />
                    <em>
                      {resume.durees[jeu].parties} partie
                      {resume.durees[jeu].parties > 1 ? "s" : ""} · de{" "}
                      {api.formatDuree(resume.durees[jeu].minSecondes)} à{" "}
                      {api.formatDuree(resume.durees[jeu].maxSecondes)}
                    </em>
                  </span>
                </div>
              ))}
              <div className="cartouche">
                <span className="cartouche__valeur">{resume.pointe?.parties ?? 0}</span>
                <span className="cartouche__nom">
                  Parties dans l'heure la plus chargée
                  {resume.pointe !== null && (
                    <>
                      <br />
                      <em>{api.formatBucket(resume.pointe.bucket, "hour")}</em>
                    </>
                  )}
                </span>
              </div>
            </div>
            <p className="panel__hint">
              Les durées ne portent que sur les expéditions <strong>complétées</strong> : une partie
              abandonnée mesurerait l'inattention, pas le parcours.
            </p>
          </section>
        </>
      )}
    </div>
  );
}
